/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { notifyAPI } from "../..";
import localforage from "localforage";
import { MenuItem } from "react-pro-sidebar";
import {
  CloseRounded,
  DoneAllRounded,
  NotificationsActiveTwoTone,
  NotificationsOffRounded,
  NotificationsTwoTone,
} from "@mui/icons-material";
import { Chip } from "../Chip";
import { useNotification, useUser } from "../UserContext";
import { CircularProgress } from "@mui/material";
import { alertYesNo } from "../../utils/utils";
import { useNavigate } from "react-router-dom";
import "./NotifyToast.css";
import { notificationActionPath } from "./notificationActions";

interface NotifyBarProps {
  isOpen: boolean;
  onClick: () => void;
}

/**
 * The sidebar row that opens the notification toast, with the unread badge.
 *
 * It is a `react-pro-sidebar` `<MenuItem>`, and that library requires a `<Menu>`
 * ancestor: `useMenu()` reads a context created with `createContext(undefined)`
 * and throws "Menu Component is required!" without one. In the app the ancestor
 * is the `<Sidebar><Menu>` in src/routes/Root.tsx; in isolation it has to be
 * asked for, which is what the line below does.
 *
 * @story provider menu
 */
export const NotifyOptionBar: React.FC<NotifyBarProps> = (props) => {
  const NotificationIcon = () => {
    const { notifications } = useNotification();
    const hasGoldenChance = notifications.some(
      (n: NotifyToastItems) => n.goldenChance,
    );
    const numberOfUnread = notifications.filter(
      (n: NotifyToastItems) => !n.isRead,
    ).length;
    const currIcon = props.isOpen ? (
      <NotificationsActiveTwoTone className="notificationBarIcon" />
    ) : (
      <NotificationsTwoTone className="notificationBarIcon" />
    );

    return (
      <div className="notificationBarIconContainer">
        {currIcon}
        {(numberOfUnread > 0 || hasGoldenChance) && !props.isOpen && (
          <div
            className={`notificationBarUnreadBadge ${
              hasGoldenChance
                ? "notifyBarBadgeGoldenChance"
                : "notifyBarBadgeNormal"
            }`}
            style={{
              padding: numberOfUnread > 0 ? "10px 8px" : "6px",
              right:
                numberOfUnread > 9
                  ? "-20px"
                  : numberOfUnread > 0
                    ? "-10px"
                    : "-2px",
              top: numberOfUnread > 0 ? "-10px" : "-4px",
            }}
          >
            {numberOfUnread > 9
              ? "9+"
              : numberOfUnread > 0
                ? numberOfUnread
                : ""}
          </div>
        )}
      </div>
    );
  };

  return (
    <MenuItem
      icon={<NotificationIcon />}
      className="notifyOptionBar"
      onClick={props.onClick}
    >
      Notificações
    </MenuItem>
  );
};
interface NotifyToastProps extends React.HTMLProps<HTMLDivElement> {
  isOpen?: boolean;
  onClose?: () => void;
}

export interface NotifyToastItems {
  id: string;
  title: string;
  message: string;
  isRead: boolean;
  goldenChance: boolean;
  expiresAt?: string;
  action?: string;
}

interface NotifyToastItemProps {
  item: NotifyToastItems;
  onClick: (id: string) => void;
}

const ChipGoldenChance: React.FC<{ expiresAt?: string }> = ({ expiresAt }) => {
  const [timeRemaining, setTimeRemaining] = React.useState<number | null>(null);
  const intervalNodeRef = React.useRef<NodeJS.Timeout | null>(null);

  function formatTime(ms: number) {
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  React.useEffect(() => {
    const updateRemainingTime = () => {
      if (!expiresAt) {
        setTimeRemaining(null);
        return;
      }
      const now = new Date();
      const expiryDate = new Date(expiresAt);
      const diff = expiryDate.getTime() - now.getTime();
      if (diff <= 0) {
        setTimeRemaining(0);
        if (intervalNodeRef.current) {
          clearInterval(intervalNodeRef.current);
        }
        return;
      }
      setTimeRemaining(diff);
    };

    updateRemainingTime();
    intervalNodeRef.current = setInterval(updateRemainingTime, 1000);
    return () => {
      if (intervalNodeRef.current) {
        clearInterval(intervalNodeRef.current);
      }
    };
    // Re-run when expiresAt changes so the countdown reflects the current
    // notification's expiry rather than the one captured on first mount.
  }, [expiresAt]);

  return (
    <Chip class="notifyToastItemFeatureChip notifyToastItemFeatureChipGoldenChance">
      <span>
        Oportunidade
        {timeRemaining !== null && timeRemaining > 0
          ? ` - ${formatTime(timeRemaining)}`
          : ""}
      </span>
    </Chip>
  );
};

const ChipUnreadNormal: React.FC = () => {
  return (
    <Chip class="notifyToastItemFeatureChip notifyToastItemFeatureChipUnreadNormal">
      <span>Novidade</span>
    </Chip>
  );
};

const NotifyToastItem = React.memo(
  ({ item, onClick }: NotifyToastItemProps) => {
    if (!item) return <></>;
    if (item.expiresAt && new Date(item.expiresAt) < new Date()) return <></>;

    return (
      <>
        <div className="notifyToastItem" onClick={() => onClick(item.id)}>
          <div className="notifyToastItemOnlyInfos">
            <span className="notifyToastItemName">{item.title}</span>
            {item.goldenChance ? (
              <ChipGoldenChance expiresAt={item.expiresAt} />
            ) : !item.isRead ? (
              <ChipUnreadNormal />
            ) : (
              <></>
            )}
          </div>
          <div className="notifyToastItemMessageContainer">
            <span className="notifyToastItemMessage">{item.message}</span>
          </div>
        </div>
      </>
    );
  },
  (prev, next) =>
    prev.item.id === next.item.id && prev.item.isRead === next.item.isRead,
);

const NoNotifications: React.FC = () => {
  return (
    <div className="noNotificationsContainer">
      <NotificationsOffRounded className="noNotificationsIcon" />
      <span>Não há notificações</span>
    </div>
  );
};

export const NotifyToast: React.FC<NotifyToastProps> = (props) => {
  const { setAmaryCourse } = useUser();
  const {
    notifications,
    setNotifications,
    fetchNotifications,
    hasMoreNotifications,
  } = useNotification();
  const [reachEnd, setReachEnd] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const navigate = useNavigate();

  const timeoutNodeRef = React.useRef<NodeJS.Timeout | null>(null);

  class APIEvents {
    static async markAsRead(id: string) {
      const JWT = (await localforage.getItem("JWT")) as string | undefined;
      if (!JWT) return false;

      const headers = {
        "X-JWT": JWT,
      };

      const notify = notifications.find(
        (notif: NotifyToastItems) => notif.id === id,
      );
      try {
        if (!notify || !notify.isRead) {
          await notifyAPI.post(`/open/${id}`, null, { headers });
          setNotifications((prevNotifications: NotifyToastItems[]) =>
            prevNotifications.map((notif) =>
              notif.id === id ? { ...notif, isRead: true } : notif,
            ),
          );
        }

        if (!notify || !notify.action) return;
        performNotificationAction(notify.action);
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    }

    // Bulk mark — prefers single round-trip via /open-all. Falls back to
    // per-item /open/{id} when the bulk endpoint isn't deployed yet (404),
    // so the feature works during the rollout window. Local state updates
    // optimistically; we only revert if even the fallback fails.
    static async markAllAsRead() {
      const JWT = (await localforage.getItem("JWT")) as string | undefined;
      if (!JWT) return;

      const headers = { "X-JWT": JWT };
      const unreadIds = notifications
        .filter((n: NotifyToastItems) => !n.isRead)
        .map((n: NotifyToastItems) => n.id);
      if (unreadIds.length === 0) return;

      const previous = notifications;
      setNotifications((prev: NotifyToastItems[]) =>
        prev.map((n) => (n.isRead ? n : { ...n, isRead: true })),
      );

      try {
        await notifyAPI.post("/open-all", null, { headers });
        return;
      } catch (error: any) {
        // Bulk endpoint missing — degrade to per-item. Anything else is a
        // real error worth surfacing.
        if (error?.response?.status !== 404) {
          console.error("Error marking all notifications as read:", error);
          setNotifications(previous);
          return;
        }
      }

      try {
        await Promise.all(
          unreadIds.map((id: string) =>
            notifyAPI.post(`/open/${id}`, null, { headers }),
          ),
        );
      } catch (error) {
        console.error("Fallback per-item mark-as-read failed:", error);
        setNotifications(previous);
      }
    }
  }

  function performNotificationAction(action: string) {
    if (action === "welcome::collaborator") {
      alertYesNo("Gostaria de fazer o tutorial?", "info", () => {
        props.onClose && props.onClose();
        navigate("/suporte");
        setAmaryCourse({
          id: "welcome",
          currentChapter: 0,
        });
      });
      return;
    }

    const path = notificationActionPath(action);
    if (!path) return;
    props.onClose && props.onClose();
    navigate(path);
  }

  function onReachEnd(e: React.UIEvent<HTMLDivElement>) {
    if (reachEnd || loadingMore || timeoutNodeRef.current) return;
    const target = e.target as HTMLDivElement;
    if (target.scrollHeight - target.scrollTop - target.clientHeight > 50)
      return;

    setLoadingMore(true);
    setReachEnd(true);

    timeoutNodeRef.current = setTimeout(async () => {
      await fetchNotifications();
      timeoutNodeRef.current = null;
      setLoadingMore(false);
      if (!hasMoreNotifications.current) return;

      setReachEnd(false);
    }, 500);
  }

  const hasUnread = notifications.some((n: NotifyToastItems) => !n.isRead);

  if (!props.isOpen) return <></>;
  return (
    <div className="notificationToastContainer">
      <div className="notificationToastTitle">
        <span>Notificações</span>
        <div className="notificationToastTitleActions">
          {hasUnread && (
            <button
              type="button"
              className="notificationToastMarkAll"
              onClick={() => APIEvents.markAllAsRead()}
              title="Marcar todas como lidas"
            >
              <DoneAllRounded />
              <span>Marcar todas como lidas</span>
            </button>
          )}
          <button onClick={props.onClose} title="Fechar">
            <CloseRounded />
          </button>
        </div>
      </div>

      {notifications.length === 0 ? (
        <NoNotifications />
      ) : (
        <div className="notifyToastItemsContainer" onScroll={onReachEnd}>
          {notifications.map((item: NotifyToastItems) => (
            <NotifyToastItem
              key={item.id}
              item={item}
              onClick={(id: string) => APIEvents.markAsRead(id)}
            />
          ))}
        </div>
      )}
      {loadingMore && (
        <div className="loadingMoreNotifications">
          <CircularProgress
            sx={{
              color: "var(--brand-9)",
            }}
          />
        </div>
      )}
    </div>
  );
};
